import {
  AppEhrState,
  AppToOSMessageTypes,
  OSToAppMessageEvent,
  OSToAppMessageTypes,
  OSToAppWorkflowEventMessageTypes,
} from '@getvim/vim-os-api';
import { EHR } from '@getvim/vim-os-sdk-api';
import equal from 'fast-deep-equal';
import { omit } from 'lodash-es';
import { EhrResourceEnhancementsExecutor, OsCommunicator } from '.';
import { SdkLogger } from '../sdkLogger';
import { InternalResourceUpdater } from './ehr-updates/internalResourceUpdater';
import { EhrResourceUpdater } from './ehr-updates/resourceUpdater';
import { OSToAppEhrConvertor } from './os-to-app-ehr-convertor/interface';
import { Subscription } from './subscription';
import { PutChartRequest } from './putChartRequest';
import { TelevisitManager } from './televisitManager';
import {
  WorkflowEventsApi,
  notifyEhrStateChangeSymbol,
  notifyOrderCreatedSymbol,
} from './workflowEvents';
import { Order } from '@getvim-os/types';

function isTelevisitClosedStatus(
  status: EHR.TelevisitState['status'],
): status is 'cancelled' | 'ended' {
  return status === 'cancelled' || status === 'ended';
}

function resourceDeepEquals<RESOURCE extends EHR.EHR_STATE_TEMPLATE[EHR.EHRResource]>(
  incomingEHRResource: RESOURCE,
  currentEhrResource: RESOURCE,
): boolean {
  const fieldsToIgnore = [
    'getProblemList',
    'getMedicationList',
    'getAllergyList',
    'getLabResults',
    'getVitals',
  ];
  const incomingResource = omit(incomingEHRResource, fieldsToIgnore);
  const currentResource = omit(currentEhrResource, fieldsToIgnore);
  return equal(incomingResource, currentResource);
}

export class EhrAPI<EHR_STATE extends EHR.EHR_STATE_TEMPLATE>
  extends Subscription<EHR.EHR_STATE_RESOURCES<EHR_STATE>>
  implements EHR.EhrAPI<EHR_STATE>
{
  #osCommunicator: OsCommunicator;
  #logger: SdkLogger | undefined;
  constructor(
    osCommunicator: OsCommunicator,
    incomingOsMessagePort: MessagePort,
    oSToAppEhrConvertor: OSToAppEhrConvertor<EHR_STATE>,
    internalResourceUpdater: InternalResourceUpdater,
    logger?: SdkLogger,
  ) {
    super({ initialValue: {}, flow: 'STATE' });
    this.#osCommunicator = osCommunicator;
    this.#logger = logger;
    this.resourceUpdater = new EhrResourceUpdater(internalResourceUpdater, logger);
    const ehrResourceEnhancements = new EhrResourceEnhancementsExecutor(osCommunicator, this);
    const putChartRequest = new PutChartRequest(osCommunicator);
    incomingOsMessagePort.addEventListener(
      'message',
      (message: OSToAppMessageEvent<{ withPII: true }>) => {
        switch (message?.data?.type) {
          case OSToAppMessageTypes.EHR_STATE_CHANGE: {
            const { updatableFields, ...incomingEhrState } = message.data.payload;
            const updatedState = this.convertIncomingEhrState(
              incomingEhrState,
              oSToAppEhrConvertor,
              ehrResourceEnhancements,
              putChartRequest,
            );
            const updatedResources: EHR.EHRResource[] = [];
            Object.values(EHR.EHRResource).forEach((resource) => {
              if (!resourceDeepEquals(this.ehrState[resource], updatedState[resource])) {
                updatedResources.push(resource);
                this.dispatch(resource, updatedState[resource]);
              }
            });

            this.workflowEvents[notifyEhrStateChangeSymbol](this.ehrState, updatedState);
            for (const resource of updatedResources) {
              // Type-safe assignment for each resource
              switch (resource) {
                case EHR.EHRResource.patient:
                  this.ehrState.patient = updatedState.patient;
                  break;
                case EHR.EHRResource.encounter:
                  this.ehrState.encounter = updatedState.encounter;
                  break;
                case EHR.EHRResource.referral:
                  this.ehrState.referral = updatedState.referral;
                  break;
                case EHR.EHRResource.orders:
                  this.ehrState.orders = updatedState.orders;
                  break;
                case EHR.EHRResource.claim:
                  this.ehrState.claim = updatedState.claim;
                  break;
                case EHR.EHRResource.customEhrData:
                  this.ehrState.customEhrData = updatedState.customEhrData;
                  break;
                default:
                  // Do nothing for unknown resources
                  break;
              }
            }
            break;
          }
          case OSToAppWorkflowEventMessageTypes.ON_ORDER_CREATED: {
            try {
              const osOrder = message.data.payload.order;
              const [order] = oSToAppEhrConvertor.convertResource(
                EHR.EHRResource.orders,
                [osOrder as Order],
                ehrResourceEnhancements,
              );
              this.workflowEvents[notifyOrderCreatedSymbol](order);
            } catch (error) {
              this.#logger?.error('Failed handling ORDER_CREATED workflow event', { error });
            }
            break;
          }
        }
      },
    );
    this.workflowEvents = new WorkflowEventsApi<EHR_STATE>();
  }

  convertIncomingEhrState(
    incomingEhrState: AppEhrState<{ withPII: true }>,
    oSToAppEhrConvertor: OSToAppEhrConvertor<EHR_STATE>,
    ehrResourceEnhancements: EhrResourceEnhancementsExecutor<EHR_STATE>,
    putChartRequest: PutChartRequest,
  ): EHR_STATE {
    const updatedState: EHR_STATE = oSToAppEhrConvertor.convertEhr(
      incomingEhrState,
      ehrResourceEnhancements,
    );
    const encounter = this.#buildEncounterState(incomingEhrState, updatedState, putChartRequest);
    return {
      ...updatedState,
      customEhrData: incomingEhrState.customEhrData,
      encounter: encounter,
    };
  }

  /**
   * Sets up the encounter object, handling both televisit and putChartRetrievalRequest logic.
   * If there is no encounter, returns undefined.
   */
  #buildEncounterState(
    incomingEhrState: AppEhrState<{ withPII: true }>,
    updatedState: EHR_STATE,
    putChartRequest: PutChartRequest,
  ): EHR_STATE['encounter'] | undefined {
    if (!updatedState.encounter) return undefined;

    return {
      ...updatedState.encounter,
      televisit: this.#buildTelevisitState(incomingEhrState),
      putChartRetrievalRequest: this.#buildChartRetrievalRequest(updatedState, putChartRequest),
    };
  }

  #buildChartRetrievalRequest(
    updatedState: EHR_STATE,
    putChartRequest: PutChartRequest,
  ): EHR.Encounter['putChartRetrievalRequest'] | undefined {
    if (
      !updatedState.encounter ||
      !updatedState.patient?.identifiers?.vimPatientId ||
      !updatedState.encounter.identifiers?.ehrEncounterId
    )
      return undefined;
    if (
      // if encounter or patient id changed, we replace the putChartRetrievalRequest
      updatedState.encounter.identifiers.ehrEncounterId !==
        this.ehrState.encounter?.identifiers?.ehrEncounterId ||
      updatedState.patient.identifiers.vimPatientId !==
        this.ehrState.patient?.identifiers?.vimPatientId
    ) {
      const encounterId = updatedState.encounter.identifiers.ehrEncounterId;
      const patientId = updatedState.patient.identifiers.ehrPatientId;
      return () => putChartRequest.putChartRetrievalRequest(encounterId, patientId);
    } else {
      // if encounter and patient id are the same, we keep the putChartRetrievalRequest
      return this.ehrState.encounter?.putChartRetrievalRequest;
    }
  }

  #buildTelevisitState(
    incomingEhrState: AppEhrState<{ withPII: true }>,
  ): EHR.TelevisitState | undefined {
    const encounterId = incomingEhrState.encounter?.encounterId;
    if (!incomingEhrState.encounter?.televisit || !encounterId) return undefined;
    if (isTelevisitClosedStatus(incomingEhrState.encounter.televisit.status)) {
      return {
        status: incomingEhrState.encounter.televisit.status,
      };
    }
    const prevTelevisit = this.ehrState.encounter?.televisit;
    const reuseTelevisit =
      prevTelevisit?.audioChannel &&
      this.ehrState.encounter?.identifiers?.ehrEncounterId === encounterId;
    if (!reuseTelevisit) {
      const televisitManager = new TelevisitManager(this.#osCommunicator, this.#logger);
      return {
        status: incomingEhrState.encounter.televisit.status,
        type: incomingEhrState.encounter.televisit.type,
        audioChannel: {
          listen: (listener: EHR.AudioListener) => televisitManager.listen(listener, encounterId),
        },
      };
    } else {
      return {
        audioChannel: prevTelevisit.audioChannel,
        status: incomingEhrState.encounter.televisit.status,
        type: incomingEhrState.encounter.televisit.type,
      };
    }
  }

  public ehrState: EHR_STATE = {} as EHR_STATE;
  public resourceUpdater: EHR.IResourceUpdater;
  public workflowEvents: WorkflowEventsApi<EHR_STATE>;
  public events = {
    emit: (event: string, payload?: any) => {
      this.#osCommunicator.sendMessageToOS({
        type: AppToOSMessageTypes.EMIT_EHR_EVENT,
        payload: { type: event, payload },
      });
    },
  };
}
